<template>
  <div class="flex flex-col shrink overflow-hidden">
    <div class="flex flex-row flex-nowrap items-center gap-3 pb-4 border-b">
      <el-button
        type="primary"
        link
        data-testid="ai-chat-prompt-list-back-button"
        @click="emit('close')"
      >
        <back-icon />
        <span class="back-button-label">{{
          t('aichat.main.chips.button.back')
        }}</span>
      </el-button>
      <span
        class="text-sm font-black"
        data-testid="ai-chat-prompt-list-title"
        >{{ t('aichat.main.chips.more') }}</span
      >
    </div>
    <div class="mt-2 mb-2 shrink overflow-auto">
      <div v-for="group in promptList" :key="group.groupId">
        <div class="flex flex-row flex-nowrap items-center gap-2 mt-2">
          <img class="icon" :src="iconRegistry[group.icon]" alt="" />
          <span
            class="text-sm font-black"
            data-testid="ai-chat-prompt-list-group-label"
            >{{ group.label }}</span
          >
        </div>
        <button
          v-for="item in group.items"
          :key="item.id"
          type="button"
          :disabled="disabled"
          class="block w-full text-left text-sm pl-6 pt-2 pb-2 border-b cursor-pointer prompt-label"
          data-testid="ai-chat-prompt-list-item-button"
          @click="ask(group, item)"
        >
          <span>{{ item.text }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { BackIcon } from '@shared/assets/icons'
import prompts from '../../constants/prompts'
import { iconRegistry } from '../../constants/prompts-icon-registry'
import type { PromptGroup, PromptItem } from '../../types'

withDefaults(
  defineProps<{
    /* the chat cannot take a question, e.g. no credits are left */
    disabled?: boolean
  }>(),
  { disabled: false }
)

/* `click` carries the prompt group, `ask` the question text that was clicked */
const emit = defineEmits<{
  click: [group: PromptGroup]
  ask: [prompt: string]
  close: []
}>()

const { t } = useI18n()
const { promptList } = prompts.setup()

const ask = (group: PromptGroup, item: PromptItem): void => {
  emit('click', group)
  emit('ask', item.text)
}
</script>

<style scoped>
.back-button-label {
  font-size: 1rem;
}

.icon {
  width: 1rem;
  height: 1rem;
}

/* a native button, so it is reachable by keyboard; these reset its default chrome */
.prompt-label {
  border-top: 0;
  border-right: 0;
  border-left: 0;
  background: none;
  color: inherit;
  font: inherit;
}

.prompt-label:not(:disabled):hover,
.prompt-label:not(:disabled):focus-visible {
  color: var(--primary-color);
}

.prompt-label:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
